import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Copy, Check, Eye, CheckCircle2, AlertTriangle, X, ChevronLeft, ChevronRight, Inbox, Filter, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getCategoriaLabel = (situacao) => {
  const CATEGORIAS = {
    MONO_CORRETO: "Produtos sujeitos ao regime monofásico ou à alíquota zero cadastrados corretamente.",
    TRIB_CORRETO: "Produtos sujeitos à tributação cadastrados de forma adequada.",
    DEVERIA_TRIBUTADO: "Produtos sujeitos à tributação enquadrados de forma incorreta no regime monofásico ou de alíquota zero.",
    DEVERIA_MONO: "Produtos enquadrados no regime monofásico ou de alíquota zero classificados indevidamente como tributados.",
    CST_DIVERGENTE: "Produtos enquadrados corretamente como monofásicos ou de alíquota zero, com código CST divergente."
  };
  
  if (situacao === CATEGORIAS.MONO_CORRETO) return "Correto";
  if (situacao === CATEGORIAS.TRIB_CORRETO) return "Correto";
  if (situacao === CATEGORIAS.DEVERIA_TRIBUTADO) return "Deve ser Tributado";
  if (situacao === CATEGORIAS.DEVERIA_MONO) return "Deve ser Monofásico";
  if (situacao === CATEGORIAS.CST_DIVERGENTE) return "CST Divergente";
  return "Outros";
};

const getCategoriaStyle = (situacao) => {
  const CATEGORIAS = {
    MONO_CORRETO: "Produtos sujeitos ao regime monofásico ou à alíquota zero cadastrados corretamente.",
    TRIB_CORRETO: "Produtos sujeitos à tributação cadastrados de forma adequada.",
  };
  
  if (situacao === CATEGORIAS.MONO_CORRETO || situacao === CATEGORIAS.TRIB_CORRETO) return "success";
  if (situacao?.includes("CST divergente")) return "warning";
  return "error";
};

export default function ProductsPage({ tipo, titulo }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // inicializa estado a partir da url
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("busca") || "");
  const [produtoIdFilter, setProdutoIdFilter] = useState(searchParams.get("produto_id") || "");
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get("page")) || 1,
    pageSize: parseInt(searchParams.get("page_size")) || 20,
    total: 0,
    totalPages: 0
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToMark, setProductToMark] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [ncmErros, setNcmErros] = useState([]);
  const [selectedNcms, setSelectedNcms] = useState(() => {
    const ncm = searchParams.get("ncm_filter");
    return ncm ? ncm.split(",") : [];
  });
  const [showNcmFilter, setShowNcmFilter] = useState(false);
  const [loadingNcmErros, setLoadingNcmErros] = useState(false);
  const [categoriaFilter, setCategoriaFilter] = useState(searchParams.get("categoria") || null);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const debounceTimer = useRef(null);

  // sincroniza estado -> url
  useEffect(() => {
    const params = new URLSearchParams();
    if (pagination.page > 1) params.set("page", pagination.page);
    if (pagination.pageSize !== 20) params.set("page_size", pagination.pageSize);
    if (debouncedSearch) params.set("busca", debouncedSearch);
    if (produtoIdFilter) params.set("produto_id", produtoIdFilter);
    if (selectedNcms.length > 0) params.set("ncm_filter", selectedNcms.join(","));
    if (categoriaFilter) params.set("categoria", categoriaFilter);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, debouncedSearch, produtoIdFilter, selectedNcms, categoriaFilter]);

  // debounce 300ms
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    fetchProdutos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, debouncedSearch, produtoIdFilter, pagination.page, selectedNcms, categoriaFilter]);

  // busca ncm erros ao abrir filtro
  const fetchNcmErros = async () => {
    if (ncmErros.length > 0) return; // ja carregou
    setLoadingNcmErros(true);
    try {
      const response = await axios.get(`${API}/ncm-erros`, { params: { tipo } });
      setNcmErros(response.data);
    } catch (error) {
      console.error("Erro ao buscar NCM erros:", error);
      toast.error("Erro ao carregar filtro de NCM");
    } finally {
      setLoadingNcmErros(false);
    }
  };

  const fetchProdutos = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize
      };
      
      if (tipo) params.tipo = tipo;
      if (debouncedSearch) params.busca = debouncedSearch;
      if (produtoIdFilter) params.produto_id = parseInt(produtoIdFilter);
      if (selectedNcms.length > 0) params.ncm_filter = selectedNcms.join(',');
      if (categoriaFilter) params.categoria = categoriaFilter;
      
      const response = await axios.get(`${API}/produtos`, { params });
      setProdutos(response.data.produtos);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopiedField(field);
      toast.success("Copiado!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Erro ao copiar");
    }
  };

  const handleMarcarCorrigido = async () => {
    if (!productToMark) return;
    
    try {
      await axios.put(`${API}/produtos/${productToMark.produto}/marcar-corrigido`);
      toast.success("Produto marcado como corrigido!");
      setShowConfirmModal(false);
      setShowModal(false);
      setProductToMark(null);
      fetchProdutos();
    } catch (error) {
      console.error("Erro ao marcar como corrigido:", error);
      toast.error("Erro ao marcar produto");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchProdutos();
  };

  const CATEGORIAS_CORRETAS = [
    "Produtos sujeitos ao regime monofásico ou à alíquota zero cadastrados corretamente.",
    "Produtos sujeitos à tributação cadastrados de forma adequada."
  ];
  const canMark = (produto) => {
    if (tipo === "corretos" || tipo === "ja_corrigidos") return false;
    if (produto.status_correcao === "corrigido") return false;
    if (CATEGORIAS_CORRETAS.includes(produto.situacao_cadastro)) return false;
    return true;
  };

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#0F172A", marginBottom: "8px" }}>
          {titulo}
        </h1>
        <p style={{ fontSize: "14px", color: "#64748B" }}>
          {pagination.total.toLocaleString()} produtos encontrados
        </p>
      </div>

      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" }}>
        {tipo === "incorretos" && (
          <button
            type="button"
            className={`btn ${showNcmFilter ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => {
              setShowNcmFilter(!showNcmFilter);
              if (!showNcmFilter) fetchNcmErros();
            }}
            style={{ padding: "8px 16px", fontSize: "13px" }}
          >
            <Filter size={14} />
            NCM com mais erros
            {showNcmFilter ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
        {tipo === "corretos" && (
          <>
            <button
              type="button"
              className={`btn ${categoriaFilter === 'Produtos sujeitos ao regime monofásico ou à alíquota zero cadastrados corretamente.' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => {
                const cat = 'Produtos sujeitos ao regime monofásico ou à alíquota zero cadastrados corretamente.';
                setCategoriaFilter(categoriaFilter === cat ? null : cat);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ padding: "8px 16px", fontSize: "13px" }}
            >
              <Filter size={14} />
              Produtos monofásicos
            </button>
            <button
              type="button"
              className={`btn ${categoriaFilter === 'Produtos sujeitos à tributação cadastrados de forma adequada.' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => {
                const cat = 'Produtos sujeitos à tributação cadastrados de forma adequada.';
                setCategoriaFilter(categoriaFilter === cat ? null : cat);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ padding: "8px 16px", fontSize: "13px" }}
            >
              <Filter size={14} />
              Produtos tributados
            </button>
          </>
        )}
        {tipo === "ja_corrigidos" && (
          <>
            <button
              type="button"
              className={`btn ${categoriaFilter === 'Produtos sujeitos à tributação enquadrados de forma incorreta no regime monofásico ou de alíquota zero.' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => {
                const cat = 'Produtos sujeitos à tributação enquadrados de forma incorreta no regime monofásico ou de alíquota zero.';
                setCategoriaFilter(categoriaFilter === cat ? null : cat);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ padding: "8px 16px", fontSize: "13px" }}
            >
              <Filter size={14} />
              Corrigidos para Tributado
            </button>
            <button
              type="button"
              className={`btn ${categoriaFilter === 'Produtos enquadrados no regime monofásico ou de alíquota zero classificados indevidamente como tributados.' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => {
                const cat = 'Produtos enquadrados no regime monofásico ou de alíquota zero classificados indevidamente como tributados.';
                setCategoriaFilter(categoriaFilter === cat ? null : cat);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ padding: "8px 16px", fontSize: "13px" }}
            >
              <Filter size={14} />
              Corrigidos para Monofásico
            </button>
            <button
              type="button"
              className={`btn ${categoriaFilter === 'Produtos enquadrados corretamente como monofásicos ou de alíquota zero, com código CST divergente.' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => {
                const cat = 'Produtos enquadrados corretamente como monofásicos ou de alíquota zero, com código CST divergente.';
                setCategoriaFilter(categoriaFilter === cat ? null : cat);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ padding: "8px 16px", fontSize: "13px" }}
            >
              <Filter size={14} />
              CST Corrigido
            </button>
          </>
        )}
        {selectedNcms.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
            {selectedNcms.map(ncm => (
              <span key={ncm} style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "4px 12px", background: "#FEF2F2", border: "1px solid #FECACA",
                borderRadius: "20px", fontSize: "12px", fontWeight: "600", color: "#DC2626"
              }}>
                NCM {ncm}
                <button onClick={() => { setSelectedNcms(prev => prev.filter(n => n !== ncm)); setPagination(prev => ({ ...prev, page: 1 })); }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "0", display: "flex" }}>
                  <X size={14} color="#DC2626" />
                </button>
              </span>
            ))}
            <button onClick={() => { setSelectedNcms([]); setPagination(prev => ({ ...prev, page: 1 })); }}
              style={{ background: "none", border: "1px solid #FECACA", borderRadius: "20px", cursor: "pointer", padding: "4px 12px", fontSize: "12px", fontWeight: "600", color: "#DC2626" }}>
              Limpar todos
            </button>
          </div>
        )}
      </div>

      {showNcmFilter && tipo === "incorretos" && (
        <div className="ncm-filter-panel">
          {loadingNcmErros ? (
            <div style={{ padding: "16px", textAlign: "center", color: "#64748B", fontSize: "13px" }}>Carregando...</div>
          ) : ncmErros.length === 0 ? (
            <div style={{ padding: "16px", textAlign: "center", color: "#64748B", fontSize: "13px" }}>Nenhum NCM encontrado</div>
          ) : (
            <div className="ncm-filter-list">
              {ncmErros.map((item) => (
                <label key={item.ncm} className={`ncm-filter-item ${selectedNcms.includes(item.ncm) ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedNcms.includes(item.ncm)}
                    onChange={() => {
                      setSelectedNcms(prev =>
                        prev.includes(item.ncm)
                          ? prev.filter(n => n !== item.ncm)
                          : [...prev, item.ncm]
                      );
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  />
                  <span className="ncm-filter-ncm">{item.ncm}</span>
                  <span className="ncm-filter-count">{item.erros} {item.erros === 1 ? 'erro' : 'erros'}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSearch} className="search-bar-container" style={{ marginBottom: "24px" }}>
        <div className="search-input-wrapper">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Buscar por NCM ou descrição do produto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <input
          type="number"
          placeholder="ID do Produto"
          value={produtoIdFilter}
          onChange={(e) => setProdutoIdFilter(e.target.value)}
          className="filter-id-input"
        />
        <button type="submit" className="search-button">
          <Search size={16} />
          Buscar
        </button>
      </form>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Carregando produtos...</p>
        </div>
      ) : produtos.length === 0 ? (
        <div className="empty-state">
          <Inbox size={64} />
          <h3>Nenhum produto encontrado</h3>
          <p>Ajuste seus filtros ou tente uma busca diferente</p>
        </div>
      ) : (
        <div className="products-table">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Produto</th>
                  <th>Status</th>
                  <th>NCM</th>
                  <th>CST</th>
                  <th>CST Sug.</th>
                  <th>Alíquota</th>
                  <th>Valor</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {produtos.map((produto) => (
                  <tr key={produto.produto}>
                    <td className="product-id-cell">#{produto.produto}</td>
                    <td className="product-name-cell">{produto.descricao}</td>
                    <td>
                      <span className={`status-badge ${getCategoriaStyle(produto.situacao_cadastro)}`}>
                        {getCategoriaLabel(produto.situacao_cadastro)}
                      </span>
                    </td>
                    <td className="data-cell">
                      {produto.ncm_sugerido && produto.ncm_sugerido !== "IGUAL" ? (
                        <div className="data-cell-group">
                          <span className="highlight-value">
                            {produto.ncm_sugerido}
                            <button
                              className="copy-button"
                              onClick={() => handleCopy(produto.ncm_sugerido, `ncm-${produto.produto}`)}
                            >
                              {copiedField === `ncm-${produto.produto}` ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                          </span>
                        </div>
                      ) : (
                        produto.ncm || "N/A"
                      )}
                    </td>
                    <td className="data-cell">{produto.cst_pis_cofins_sistema ?? "N/A"}</td>
                    <td className="data-cell">
                      {produto.cst_sugerido && String(produto.cst_sugerido) !== String(produto.cst_pis_cofins_sistema) ? (
                        <span className="highlight-value">
                          {produto.cst_sugerido}
                          <button
                            className="copy-button"
                            onClick={() => handleCopy(produto.cst_sugerido, `cst-${produto.produto}`)}
                          >
                            {copiedField === `cst-${produto.produto}` ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        </span>
                      ) : (
                        produto.cst_sugerido || "N/A"
                      )}
                    </td>
                    <td className="data-cell">
                      {produto.aliquota_correta !== null ? (
                        <div className="data-cell-group">
                          {produto.aliquota_correta}%
                          <button
                            className="copy-button"
                            onClick={() => handleCopy(produto.aliquota_correta, `aliquota-${produto.produto}`)}
                          >
                            {copiedField === `aliquota-${produto.produto}` ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        </div>
                      ) : "N/A"}
                    </td>
                    <td className="data-cell">R$ {produto.valor_total?.toFixed(2) || "0.00"}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-outline"
                          onClick={() => { setSelectedProduct(produto); setShowModal(true); }}
                        >
                          <Eye size={14} />
                          Detalhes
                        </button>
                        {canMark(produto) && (
                          <button
                            className="btn btn-primary"
                            onClick={() => { setProductToMark(produto); setShowConfirmModal(true); }}
                          >
                            <CheckCircle2 size={14} />
                            Marcar como Corrigido
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Mostrando {((pagination.page - 1) * pagination.pageSize) + 1} a {Math.min(pagination.page * pagination.pageSize, pagination.total)} de {pagination.total} produtos
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft size={16} />
                  Anterior
                </button>
                <button
                  className="pagination-button"
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Próxima
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* modal detalhes */}
      {showModal && selectedProduct && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>#{selectedProduct.produto} - {selectedProduct.descricao}</h2>
              <p>Detalhes completos do produto</p>
            </div>
            <div className="modal-body">
              <div className="modal-field-grid">
                <div className="modal-field">
                  <div className="modal-field-label">NCM Atual</div>
                  <div className="modal-field-value">{selectedProduct.ncm || "N/A"}</div>
                </div>
                {selectedProduct.ncm_sugerido && selectedProduct.ncm_sugerido !== "IGUAL" && (
                  <div className="modal-field">
                    <div className="modal-field-label">NCM Sugerido</div>
                    <div className="modal-field-value">
                      <span className="highlight-value">{selectedProduct.ncm_sugerido}</span>
                      <button className="copy-button" style={{ opacity: 1 }} onClick={() => handleCopy(selectedProduct.ncm_sugerido, "modal-ncm")}>
                        {copiedField === "modal-ncm" ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="modal-field">
                  <div className="modal-field-label">CST Sistema</div>
                  <div className="modal-field-value">{selectedProduct.cst_pis_cofins_sistema ?? "N/A"}</div>
                </div>
                <div className="modal-field">
                  <div className="modal-field-label">CST Sugerido</div>
                  <div className="modal-field-value">
                    <span className="highlight-value">{selectedProduct.cst_sugerido}</span>
                    <button className="copy-button" style={{ opacity: 1 }} onClick={() => handleCopy(selectedProduct.cst_sugerido, "modal-cst")}>
                      {copiedField === "modal-cst" ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
                <div className="modal-field">
                  <div className="modal-field-label">Alíquota Correta</div>
                  <div className="modal-field-value">
                    {selectedProduct.aliquota_correta}%
                    <button className="copy-button" style={{ opacity: 1 }} onClick={() => handleCopy(selectedProduct.aliquota_correta, "modal-aliquota")}>
                      {copiedField === "modal-aliquota" ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
                <div className="modal-field">
                  <div className="modal-field-label">Tributação Correta</div>
                  <div className="modal-field-value">{selectedProduct.tributacao_correta}</div>
                </div>
                <div className="modal-field" style={{ gridColumn: "1 / -1" }}>
                  <div className="modal-field-label">Situação do Cadastro</div>
                  <p style={{ fontSize: "13px", lineHeight: "1.6", color: "#64748B", marginTop: "6px" }}>
                    {selectedProduct.situacao_cadastro}
                  </p>
                </div>
                {selectedProduct.aviso && (
                  <div className="modal-alert">
                    <AlertTriangle className="modal-alert-icon" size={18} />
                    <div className="modal-alert-text">{selectedProduct.aviso}</div>
                  </div>
                )}
              </div>
            </div>
            {selectedProduct && canMark(selectedProduct) && (
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setShowModal(false)}>Fechar</button>
                <button className="btn btn-primary" onClick={() => { setProductToMark(selectedProduct); setShowConfirmModal(true); }}>
                  <CheckCircle2 size={14} />
                  Marcar como Corrigido
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* modal confirmacao */}
      {showConfirmModal && productToMark && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h2>Confirmar Correção</h2>
              <p>Esta ação não pode ser desfeita</p>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: "14px", marginBottom: "16px" }}>
                Você tem certeza que deseja marcar o produto como corrigido?
              </p>
              <div style={{ padding: "16px", background: "#F8FAFC", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                <div className="modal-field-label">Produto</div>
                <p style={{ fontWeight: "600", margin: "8px 0", fontSize: "14px" }}>{productToMark.descricao}</p>
                <span className="product-id-cell">#{productToMark.produto}</span>
              </div>
              <div className="modal-alert" style={{ marginTop: "16px" }}>
                <AlertTriangle className="modal-alert-icon" size={16} />
                <div className="modal-alert-text" style={{ fontSize: "12px" }}>
                  Ao confirmar, este produto será movido para a aba "Corrigidos".
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowConfirmModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleMarcarCorrigido}>
                <CheckCircle2 size={14} />
                Confirmar Correção
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
